/**
 * =============================================================================
 * IDS CRM Web - Template Check Form Renderer
 * =============================================================================
 *
 * PURPOSE:
 * Reusable component that renders a check form from a template schema.
 * Manages answer state keyed by field_key, supports follow-up fields,
 * and provides validation helpers.
 *
 * Workscope forms Phase 1 (Gareth 2026-10-22) adds the question types and
 * rules a workscope needs, read through utils/formRules.js (the same rules the
 * server checks): readings with a unit and acceptable range, pick one / pick
 * several, dates, photo and file questions, instruction text, signatures;
 * questions that show only when an earlier answer calls for them; evidence
 * (photos) optional, required, or required on a fail; and the note a failed
 * answer demands. Today's question types render exactly as before.
 *
 * USAGE:
 * <TemplateCheckFormRenderer
 *   schema={templateSchema}
 *   answers={answers}
 *   onAnswersChange={setAnswers}
 *   disabled={false}
 *   showValidationErrors={false}
 * />
 *
 * =============================================================================
 */

import React from 'react';
import PropTypes from 'prop-types';
import './TemplateCheckFormRenderer.css';
import SignaturePad from './SignaturePad';
import { authedFileHref } from '../utils/files';
import {
  outstandingItems,
  questionsByKey,
  isQuestionVisible,
  answerFails,
  evidenceOf,
  NOTE_SUFFIX,
  ATTACHMENT_STEP_PREFIX
} from '../utils/formRules';

const FOLLOW_UP_SUPPORTED_TYPES = ['yes_no', 'yes_no_na'];

/**
 * Validate answers against the template schema.
 * Returns { valid: boolean, errors: Array<{ field_key, message, section?, fieldLabel? }> }
 * options.attachments: the form's photos and files (step-keyed), for the
 * questions that need them.
 */
export function validateAnswers(schema, answers, options = {}) {
  const { requireComplete = true, attachments = {} } = options;
  if (!schema?.sections || !requireComplete) {
    return { valid: true, errors: [] };
  }
  // The rules (show-when, fails, required photos) live in utils/formRules.js,
  // shared word for word with the server.
  const errors = outstandingItems(schema, answers, attachments);
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Check if a field's answer indicates a "fail": a Yes/No fail value, a
 * reading outside its range, a choice marked as a fail.
 */
export function isFailAnswer(field, answer) {
  // A question with no type set reads its fail value as a Yes/No always did.
  if (field && !field.field_type) return answerFails({ ...field, field_type: 'yes_no' }, answer);
  return answerFails(field, answer);
}

/**
 * Get active sections from schema, sorted by order.
 */
function getActiveSections(schema) {
  if (!schema?.sections) return [];
  return schema.sections
    .filter(s => !s.archived)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Get active fields from a section, sorted by order.
 */
function getActiveFields(section) {
  if (!section?.fields) return [];
  return section.fields
    .filter(f => !f.archived)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

// A file with no type recorded is shown as a photo, as it always was.
const isImage = (att) => {
  if (!att) return false;
  if (att.type) return String(att.type).startsWith('image/');
  const inline = att.data || att.dataUrl;
  if (inline && String(inline).startsWith('data:')) return String(inline).startsWith('data:image');
  return true;
};

/** Where a photo or file can be seen: a saved file, a picked file, or only its name. */
const attachmentHref = (att) => {
  if (att?.file_url) return att.file_url;
  if (att?.data) return att.data;
  if (att?.dataUrl) return att.dataUrl;
  if (att?.url) return authedFileHref(att.url);
  return null;
};

const rangeText = (field) => {
  const unit = field.unit ? ` ${field.unit}` : '';
  const hasMin = field.min !== undefined && field.min !== null && field.min !== '';
  const hasMax = field.max !== undefined && field.max !== null && field.max !== '';
  if (hasMin && hasMax) return `Acceptable: ${field.min} to ${field.max}${unit}`;
  if (hasMin) return `Acceptable: at least ${field.min}${unit}`;
  if (hasMax) return `Acceptable: up to ${field.max}${unit}`;
  return null;
};

function TemplateCheckFormRenderer({
  schema,
  answers = {},
  onAnswersChange,
  disabled = false,
  showValidationErrors = false,
  validationErrors = [],
  stepAttachments = {},
  onAddPhotos,
  onRemovePhoto
}) {
  const byKey = questionsByKey(schema);

  const handleAnswerChange = (fieldKey, value) => {
    if (onAnswersChange) {
      onAnswersChange({
        ...answers,
        [fieldKey]: value
      });
    }
  };

  const getFieldError = (fieldKey) => {
    if (!showValidationErrors) return null;
    return validationErrors.find(e => e.field_key === fieldKey);
  };

  // The note and photo a failed answer demands are shown at their own place.
  const getReasonError = (fieldKey, reason) => {
    if (!showValidationErrors) return null;
    return validationErrors.find(e => e.field_key === fieldKey && e.reason === reason);
  };
  const getAnswerError = (fieldKey) => {
    if (!showValidationErrors) return null;
    return validationErrors.find(e => e.field_key === fieldKey && e.reason !== 'note' && e.reason !== 'photo');
  };

  const renderYesNoField = (field) => {
    const value = answers[field.field_key];
    const error = getAnswerError(field.field_key);

    return (
      <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
        <select
          value={value ?? ''}
          onChange={(e) => handleAnswerChange(field.field_key, e.target.value)}
          disabled={disabled}
          className="tcfr-select"
        >
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderYesNoNaField = (field) => {
    const value = answers[field.field_key];
    const error = getAnswerError(field.field_key);

    return (
      <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
        <select
          value={value ?? ''}
          onChange={(e) => handleAnswerChange(field.field_key, e.target.value)}
          disabled={disabled}
          className="tcfr-select"
        >
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
          <option value="na">N/A</option>
        </select>
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderTextField = (field) => {
    const value = answers[field.field_key];
    const error = getAnswerError(field.field_key);

    return (
      <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
        <textarea
          value={value ?? ''}
          onChange={(e) => handleAnswerChange(field.field_key, e.target.value)}
          disabled={disabled}
          className="tcfr-textarea"
          placeholder={field.placeholder || 'Enter text...'}
          rows={3}
        />
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderNumberField = (field) => {
    const value = answers[field.field_key];
    const error = getAnswerError(field.field_key);

    return (
      <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => handleAnswerChange(field.field_key, e.target.value)}
          disabled={disabled}
          className="tcfr-input"
          placeholder={field.placeholder || 'Enter number...'}
        />
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderCheckboxField = (field) => {
    const value = answers[field.field_key];
    const error = getAnswerError(field.field_key);

    return (
      <div className={`tcfr-field-input checkbox ${error ? 'has-error' : ''}`}>
        <label className="tcfr-checkbox-label">
          <input
            type="checkbox"
            checked={value === true || value === 'true'}
            onChange={(e) => handleAnswerChange(field.field_key, e.target.checked)}
            disabled={disabled}
          />
          <span className="tcfr-checkbox-text">{field.label}</span>
        </label>
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  // A reading: a number in its unit, with the acceptable range shown and a
  // reading outside it marked as it is typed.
  const renderReadingField = (field) => {
    const value = answers[field.field_key];
    const error = getAnswerError(field.field_key);
    const range = rangeText(field);
    const outside = answerFails(field, value);
    const inputId = `tcfr-${field.field_key}`;

    return (
      <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
        <div className="tcfr-reading">
          <input
            id={inputId}
            type="text"
            inputMode="decimal"
            value={value ?? ''}
            onChange={(e) => handleAnswerChange(field.field_key, e.target.value)}
            disabled={disabled}
            className="tcfr-input tcfr-reading-input"
            placeholder={field.placeholder || 'Reading'}
            aria-label={field.label}
          />
          {field.unit && <span className="tcfr-reading-unit">{field.unit}</span>}
        </div>
        {range && <span className={`tcfr-reading-range ${outside ? 'outside' : ''}`}>{outside ? `Outside the acceptable range. ${range}` : range}</span>}
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderChoiceField = (field, several) => {
    const value = answers[field.field_key];
    const chosen = several ? (Array.isArray(value) ? value.map(String) : []) : null;
    const error = getAnswerError(field.field_key);
    const name = `tcfr-${field.field_key}`;

    return (
      <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
        <div className="tcfr-choices" role={several ? 'group' : 'radiogroup'} aria-label={field.label}>
          {(field.options || []).map(option => {
            const checked = several ? chosen.includes(String(option.value)) : String(value ?? '') === String(option.value);
            return (
              <label key={option.value} className={`tcfr-choice ${checked ? 'checked' : ''}`}>
                <input
                  type={several ? 'checkbox' : 'radio'}
                  aria-label={option.label}
                  name={name}
                  value={option.value}
                  checked={checked}
                  disabled={disabled}
                  onChange={(e) => {
                    if (!several) {
                      handleAnswerChange(field.field_key, option.value);
                      return;
                    }
                    const next = e.target.checked
                      ? [...chosen, String(option.value)]
                      : chosen.filter(item => item !== String(option.value));
                    handleAnswerChange(field.field_key, next);
                  }}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderDateField = (field) => {
    const value = answers[field.field_key];
    const error = getAnswerError(field.field_key);

    return (
      <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
        <input
          type="date"
          value={value ?? ''}
          onChange={(e) => handleAnswerChange(field.field_key, e.target.value)}
          disabled={disabled}
          className="tcfr-input tcfr-date"
          aria-label={field.label}
        />
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderSignatureField = (field) => {
    const error = getAnswerError(field.field_key);
    return (
      <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
        <SignaturePad
          value={answers[field.field_key] || ''}
          onChange={(dataUrl) => handleAnswerChange(field.field_key, dataUrl)}
          disabled={disabled}
          label={field.label}
        />
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  // Photos and files under one question: the picker, what is already there,
  // and (for photo and file questions) how many are needed.
  const renderAttachments = (field, { kind = 'photo', buttonText = 'Add Photo', needed = null, error = null } = {}) => {
    const stepKey = `${ATTACHMENT_STEP_PREFIX}${field.field_key}`;
    const attachments = stepAttachments[stepKey] || [];
    const accept = kind === 'file' ? 'image/*,.pdf,.txt,application/pdf,text/plain' : 'image/*';

    return (
      <div className={`tcfr-photo-section ${error ? 'has-error' : ''}`}>
        <label className="tcfr-photo-upload-label">
          <input
            type="file"
            accept={accept}
            multiple
            disabled={disabled || !onAddPhotos}
            onChange={(e) => {
              if (onAddPhotos && e.target.files?.length > 0) {
                onAddPhotos(stepKey, e.target.files, e.target);
              }
            }}
            style={{ display: 'none' }}
          />
          <span className="tcfr-photo-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21,15 16,10 5,21"/>
            </svg>
            <span>{buttonText}</span>
          </span>
        </label>
        {needed && (
          <span className="tcfr-photo-needed">{attachments.length} of {needed} added</span>
        )}
        {attachments.length > 0 && (
          <div className="tcfr-photo-thumbnails">
            {attachments.map((att, idx) => {
              const href = attachmentHref(att);
              return (
                <div key={att.id || att.attachment_id || att.client_ref || `new-${idx}`} className={`tcfr-photo-thumb ${isImage(att) ? '' : 'file'}`}>
                  {href && isImage(att) ? (
                    <img src={href} alt={att.filename || att.name || 'Photo'} />
                  ) : href ? (
                    <a className="tcfr-photo-name" href={href} target="_blank" rel="noopener noreferrer">{att.filename || att.name || 'File'}</a>
                  ) : (
                    <span className="tcfr-photo-name">{att.filename || att.name || 'Photo'}</span>
                  )}
                  {!disabled && onRemovePhoto && (
                    <button
                      type="button"
                      className="tcfr-photo-remove"
                      onClick={() => onRemovePhoto(stepKey, idx)}
                      title={kind === 'file' ? 'Remove file' : 'Remove photo'}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderPhotoOrFileQuestion = (field) => {
    const needed = Math.max(1, Number(field.min_count) || 1);
    const isFile = field.field_type === 'file';
    return renderAttachments(field, {
      kind: isFile ? 'file' : 'photo',
      buttonText: isFile ? 'Add File' : 'Add Photo',
      needed: field.required_on_complete ? needed : null,
      error: getAnswerError(field.field_key)
    });
  };

  // Evidence on an ordinary question: offered when the builder allows photos,
  // demanded when required or when the answer fails and the builder says so.
  const renderEvidence = (field, fails) => {
    if (field.field_type === 'photo' || field.field_type === 'file' || field.field_type === 'instruction') return null;
    const evidence = evidenceOf(field);
    const demanded = evidence === 'required' || (fails && (evidence === 'on_fail' || field.on_fail?.photo));
    if (evidence === 'none' && !demanded) return null;
    if (evidence === 'on_fail' && !fails && !field.on_fail?.photo) return null;
    return renderAttachments(field, {
      buttonText: demanded ? 'Add Photo (required)' : 'Add Photo',
      error: getReasonError(field.field_key, 'photo')
    });
  };

  const renderFailNote = (field, fails) => {
    if (!fails || !field.on_fail?.note) return null;
    const noteKey = `${field.field_key}${NOTE_SUFFIX}`;
    const error = getReasonError(field.field_key, 'note');
    return (
      <div className={`tcfr-fail-note ${error ? 'has-error' : ''}`}>
        <label className="tcfr-fail-note-label" htmlFor={`tcfr-${noteKey}`}>What is wrong? <span className="tcfr-required">*</span></label>
        <textarea
          id={`tcfr-${noteKey}`}
          value={answers[noteKey] ?? ''}
          onChange={(e) => handleAnswerChange(noteKey, e.target.value)}
          disabled={disabled}
          className="tcfr-textarea"
          placeholder="Say what you found and what was done"
          rows={2}
        />
        {error && <span className="tcfr-field-error">{error.message}</span>}
      </div>
    );
  };

  const renderFieldInput = (field) => {
    switch (field.field_type) {
      case 'yes_no':
        return renderYesNoField(field);
      case 'yes_no_na':
        return renderYesNoNaField(field);
      case 'text':
        return renderTextField(field);
      case 'number':
        return renderNumberField(field);
      case 'checkbox':
        return renderCheckboxField(field);
      case 'reading':
        return renderReadingField(field);
      case 'choice':
        return renderChoiceField(field, false);
      case 'multi_choice':
        return renderChoiceField(field, true);
      case 'date':
        return renderDateField(field);
      case 'signature':
        return renderSignatureField(field);
      case 'photo':
      case 'file':
        return renderPhotoOrFileQuestion(field);
      default:
        return (
          <div className="tcfr-field-input">
            <span className="tcfr-unsupported">Unsupported field type: {field.field_type}</span>
          </div>
        );
    }
  };

  const renderFollowUp = (field) => {
    if (!field.follow_up?.field) return null;
    if (field.follow_up.field.archived) return null;
    if (!FOLLOW_UP_SUPPORTED_TYPES.includes(field.field_type)) return null;

    const parentAnswer = answers[field.field_key];
    const conditionMet = String(parentAnswer) === String(field.follow_up.condition);

    if (!conditionMet) return null;

    const followUpField = field.follow_up.field;
    const error = getFieldError(followUpField.field_key);
    const conditionLabel = field.follow_up.condition === 'true' ? 'Yes'
      : field.follow_up.condition === 'false' ? 'No'
      : field.follow_up.condition === 'na' ? 'N/A'
      : field.follow_up.condition;

    return (
      <div className="tcfr-follow-up">
        <div className="tcfr-follow-up-indicator">
          <span className="tcfr-follow-up-arrow">↳</span>
          <span className="tcfr-follow-up-condition">When "{conditionLabel}" is selected:</span>
        </div>
        <div className="tcfr-follow-up-field">
          <div className="tcfr-field-label">
            <span>{followUpField.label}</span>
            {followUpField.required_on_complete && <span className="tcfr-required">*</span>}
          </div>
          <div className={`tcfr-field-input ${error ? 'has-error' : ''}`}>
            <textarea
              value={answers[followUpField.field_key] ?? ''}
              onChange={(e) => handleAnswerChange(followUpField.field_key, e.target.value)}
              disabled={disabled}
              className="tcfr-textarea"
              placeholder={followUpField.placeholder || 'Enter details...'}
              rows={2}
            />
            {error && <span className="tcfr-field-error">{error.message}</span>}
          </div>
        </div>
      </div>
    );
  };

  const renderInstruction = (field) => (
    <div key={field.field_key} className="tcfr-field tcfr-instruction" data-field-key={field.field_key} role="note">
      <div className="tcfr-instruction-title">{field.label}</div>
      {field.body && <p className="tcfr-instruction-body">{field.body}</p>}
    </div>
  );

  const renderField = (field) => {
    if (!isQuestionVisible(field, answers, byKey)) return null;
    if (field.field_type === 'instruction') return renderInstruction(field);

    const isCheckbox = field.field_type === 'checkbox';
    const fails = answerFails(field, answers[field.field_key]);
    const dependent = Boolean(field.show_when?.field_key);

    return (
      <div key={field.field_key} className={`tcfr-field ${dependent ? 'tcfr-dependent' : ''} ${fails ? 'tcfr-fails' : ''}`} data-field-key={field.field_key}>
        {!isCheckbox && (
          <div className="tcfr-field-label">
            <span>{field.label}</span>
            {field.required_on_complete && <span className="tcfr-required">*</span>}
            {fails && <span className="tcfr-fail-chip">Fail</span>}
          </div>
        )}
        {field.help_text && <p className="tcfr-help-text">{field.help_text}</p>}
        {renderFieldInput(field)}
        {renderEvidence(field, fails)}
        {renderFailNote(field, fails)}
        {renderFollowUp(field)}
      </div>
    );
  };

  const renderSection = (section) => {
    const activeFields = getActiveFields(section);

    return (
      <div key={section.section_key} className="tcfr-section" data-section-key={section.section_key}>
        <div className="tcfr-section-header">
          <h3>{section.title}</h3>
        </div>
        <div className="tcfr-section-fields">
          {activeFields.map(renderField)}
          {activeFields.length === 0 && (
            <p className="tcfr-empty-section">No fields in this section.</p>
          )}
        </div>
      </div>
    );
  };

  if (!schema?.sections) {
    return (
      <div className="tcfr-empty">
        <p>No form schema available.</p>
      </div>
    );
  }

  const activeSections = getActiveSections(schema);

  if (activeSections.length === 0) {
    return (
      <div className="tcfr-empty">
        <p>No active sections in this form.</p>
      </div>
    );
  }

  return (
    <div className="template-check-form-renderer">
      {schema.name && (
        <div className="tcfr-header">
          <h2>{schema.name}</h2>
          {schema.description && <p className="tcfr-description">{schema.description}</p>}
        </div>
      )}
      <div className="tcfr-sections">
        {activeSections.map(renderSection)}
      </div>
      <div className="tcfr-footer">
        <p className="tcfr-required-note">* Required fields</p>
      </div>
    </div>
  );
}

const fieldShape = {
  field_key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  field_type: PropTypes.string.isRequired,
  order: PropTypes.number,
  archived: PropTypes.bool,
  required_on_complete: PropTypes.bool,
  allow_photos: PropTypes.bool,
  evidence: PropTypes.oneOf(['none', 'optional', 'required', 'on_fail']),
  on_fail: PropTypes.shape({ note: PropTypes.bool, photo: PropTypes.bool }),
  show_when: PropTypes.shape({
    field_key: PropTypes.string,
    is: PropTypes.string,
    values: PropTypes.array
  }),
  unit: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    fails: PropTypes.bool
  })),
  min_count: PropTypes.number,
  body: PropTypes.string,
  help_text: PropTypes.string
};

TemplateCheckFormRenderer.propTypes = {
  schema: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.shape({
      section_key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      order: PropTypes.number,
      archived: PropTypes.bool,
      fields: PropTypes.arrayOf(PropTypes.shape({
        ...fieldShape,
        follow_up: PropTypes.shape({
          condition: PropTypes.string,
          field: PropTypes.shape({
            field_key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            field_type: PropTypes.string.isRequired,
            required_on_complete: PropTypes.bool,
            archived: PropTypes.bool
          })
        })
      }))
    }))
  }),
  answers: PropTypes.object,
  onAnswersChange: PropTypes.func,
  disabled: PropTypes.bool,
  showValidationErrors: PropTypes.bool,
  validationErrors: PropTypes.arrayOf(PropTypes.shape({
    field_key: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired
  })),
  stepAttachments: PropTypes.object,
  onAddPhotos: PropTypes.func,
  onRemovePhoto: PropTypes.func
};

export default TemplateCheckFormRenderer;
